import {
  isAssetReadOnly,
  useCurrentAccount,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AddIcon,
  Button,
  CardButton,
  SlideAction,
  tokens,
  useSlideLayout
} from '@gripp/shared-ui';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

type AssetMediaFoldProps = {
  workspaceAsset: WorkspaceAsset | undefined;
  assetId: string;
  forceShowing?: boolean;
};

export const AssetMediaFold: React.FC<AssetMediaFoldProps> = ({
  workspaceAsset,
  assetId,
  forceShowing,
}) => {
  const { issueId } = useParams();
  const location = useLocation();
  const isOnAssetPage = location.pathname.startsWith('/assets/');
  const { t } = useTranslation();
  const mediaData = useMemo(
    () => workspaceAsset?.asset?.media || [],
    [workspaceAsset?.asset?.media]
  );
  const displayMediaData = useMemo(() => mediaData.slice(0, 3), [mediaData]);
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const navigate = useNavigate();
  const { onSlideChange } = useSlideLayout();
  const { basePath } = useAssetRouting(assetId);
  const isFilesPage = location.pathname.includes('/files');

  const handleFilePress = useCallback(
    async (fileId: string) => {
      if (!isFilesPage) {
        await onSlideChange(SlideAction.NEXT);
      }
      // Pass source so AssetFilePreview knows to show the AssetCard in the
      // left panel instead of the file list.
      navigate(`${basePath}/files/${fileId}`, {
        state: { source: 'assetCard' },
      });
    },
    [navigate, basePath, onSlideChange, isFilesPage]
  );

  const handleViewAllFiles = useCallback(async () => {
    if (!isFilesPage) {
      await onSlideChange(SlideAction.NEXT);
    }

    navigate(`${basePath}/files`);
  }, [navigate, basePath, onSlideChange, isFilesPage]);

  if (!workspaceAsset || (!forceShowing && issueId && isOnAssetPage)) return null;

  return (
    <View
      style={[
        styles.containerHeightCropCircle,
        !displayMediaData.length
          ? { height: 100 }
          : { height: displayMediaData.length * 60 + 32 },
      ]}
    >
      <ViewAllSectionHeader
        title={t('asset.files.title')}
        onPress={handleViewAllFiles}
      />
      {displayMediaData.length > 0 ? (
        <View style={[styles.listContainer]}>
          <FlatList
            data={displayMediaData}
            keyExtractor={(item, index) =>
              item.id?.toString() || index.toString()
            }
            scrollEnabled={false}
            renderItem={({ item }) => (
              <View style={styles.cardButtonContainer}>
                <CardButton
                  text={item.name}
                  color="white"
                  leftIcon="Document B"
                  rightIcon="Chevron Right"
                  showAvatars={false}
                  onPress={item.id ? () => handleFilePress(item.id!) : undefined}
                  bordered={false}
                />
              </View>
            )}
            showsVerticalScrollIndicator={true}
          />
        </View>
      ) : isReadOnlyMode ? null : (
        <Button
          style={styles.addFileEmpty}
          onPress={async () => {
            if (!isFilesPage) {
              await onSlideChange(SlideAction.NEXT);
            }
            navigate(`${basePath}/files`, { state: { openAddSheet: true } });
          }}
          leftIcon={<AddIcon />}
          variant={'rectangular'}
        >
          {t('asset.files.addFile')}
        </Button>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  containerHeightCropCircle: {
    maxHeight: 262,
    minHeight: 80,
    height: '100%',
    marginBottom: tokens.spacing.xl,
    paddingHorizontal: tokens.spacing.lg,
  },
  listContainer: {
    maxHeight: 184,
    overflow: 'hidden',
    marginBottom: 54,
  },
  addFileEmpty: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    backgroundColor: tokens.colors.slate[100],
  },
  cardButtonContainer: {
    marginBottom: tokens.spacing.sm,
  },
});
